import React, { useEffect, useState } from 'react';
import { Card, CardBody, Input, Button, InputGroup, Table, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import InputModal from '../../components/common/InputModal';
import AlertModal from '../common/AlertModal';
// import LoaderButton from '../loader-button/LoaderButton';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Heading from '../common/Heading';
import Loader from '../common/Loader';
// services
import BZAPIService from '../../services/BZAPIService';
//libs
import { adminIsReadOnlyCheck } from '../../helpers/rbac';
// constants
import { aws } from '../../config';

const OpenApiSettings = () => {
  const [keys, setKeys] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [isShow, setIsShow] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [keyId, setKeyId] = useState('');
  const [apiKeysEnabled, setApiKeysEnabled] = useState(true);

  useEffect(() => {
    async function onLoad() {
      try {
        // Load permissions first so a failure in an unrelated call below can't leave the page stuck as read-only
        const myUserPermissions = await BZAPIService.getMyUserPermissions();
        setIsReadOnly(adminIsReadOnlyCheck(myUserPermissions));

        // Get vendors to check if API key creation is enabled
        const bugZeroApiEnabled = await BZAPIService.getTenantSetting('bugZeroApiEnabled');

        console.log('bugZeroApiEnabled', bugZeroApiEnabled);
        // Set API keys enabled only if bugZeroApiEnabled exists and is active
        const isApiEnabled = bugZeroApiEnabled?.value?.value || false;
        setApiKeysEnabled(isApiEnabled);

        // Always fetch API keys to display existing keys even when API is disabled
        const apiKeys = (await BZAPIService.getKeys()) || {};
        setKeys(apiKeys ? apiKeys.data.items : []);

        setIsLoading(false);
      } catch (e) {
        console.log(e);
        setIsLoading(false);
      }
    }
    onLoad();
    // console.log('api', aws.apiGateway.URL);
    setApiBaseUrl(aws.openApiGateway.URL);
  }, []);

  const createKey = async () => {
    console.log('name', keyName);
    setIsSubmitted(true);
    try {
      setLoader(true);
      // Create new Open API Key
      const res = await BZAPIService.createKey({ name: keyName });
      setKeys(res);
      toast.success('API key created');
      const apiKeys = (await BZAPIService.getKeys()) || {};
      console.log(apiKeys.data.items);
      setKeys(apiKeys ? apiKeys.data.items : []);
      setKeyName('');
      setLoader(false);
      setModalOpen(false);
    } catch (e) {
      console.log('e', e);
      setKeyName('');
      setLoader(false);
      toast.error('Failed to create key');
    }
  };

  const deleteKey = async keyId => {
    console.log(keyId);
    try {
      setLoader(true);
      // Create new Open API Key
      await BZAPIService.deleteKey(keyId);
      toast.success('API deleted');
      const apiKeys = (await BZAPIService.getKeys()) || {};
      console.log(apiKeys.data.items);
      setKeys(apiKeys ? apiKeys.data.items : []);
      setLoader(false);
      setShowAlert(false);
    } catch (e) {
      console.log('e', e);
      setLoader(false);
      toast.error('Failed to delete key');
    }
  };

  const toggleKeyStatus = async (keyId, enabled) => {
    try {
      setLoader(true);
      // Toggle key status
      const newStatus = enabled ? 0 : 1;
      await BZAPIService.enableKey(keyId, newStatus);
      toast.success(`API key ${newStatus ? 'enabled' : 'disabled'}`);

      // Refresh keys
      const apiKeys = (await BZAPIService.getKeys()) || {};
      setKeys(apiKeys ? apiKeys.data.items : []);
      setLoader(false);
    } catch (e) {
      console.log('e', e);
      setLoader(false);
      toast.error(`Failed to ${enabled ? 'disable' : 'enable'} API key`);
    }
  };

  // API key creation is now managed in SuperAdmin page through the bugzeroapi vendor toggle

  const toggleShow = v => {
    v === isShow ? setIsShow('') : setIsShow(v);
  };

  const toggleAlert = (v, n) => {
    setKeyId(v);
    setKeyName(n);
    setShowAlert(true);
  };

  const handleSave = async () => {};

  // Determine if actions should be disabled (either read-only user or API disabled)
  const actionsDisabled = isReadOnly || !apiKeysEnabled;

  if (isLoading && !isSubmitted) return <Loader text="Loading API settings..." />;
  return (
    <>
      <Heading title="BugZero API" subtitle="Generate or Manage your API Keys" className="mb-4 mt-3" icon="cog" />

      {/* Show warning if API keys are disabled */}
      {!apiKeysEnabled && (
        <Alert color="warning">
          API key management is unavailable because the BugZero API integration has been disabled. Please contact your
          administrator to enable this feature in BugZero.
        </Alert>
      )}

      {isReadOnly && apiKeysEnabled && (
        <Alert color="info">Note: Your user only has read-only access. You will not be able to save any updates.</Alert>
      )}

      <Card>
        <CardBody className="fs--1">
          <div className="row">
            <div className="col">
              <p>
                <strong>API Base URL:</strong>{' '}
                <span className="active ml-2" data-testid={'api-url'}>
                  {apiBaseUrl}
                </span>
              </p>
            </div>

            <div className="col text-right">
              <a
                href={`/documentation/sso-sn-integration-guides/bugzero-api-integration-guide`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-falcon-default ml-2 btn-sm"
                data-testid={'integration-link'}
              >
                <FontAwesomeIcon icon="book" className="mr-2" />
                BugZero API Guide
              </a>
            </div>
          </div>

          <div />
          <Table bordered striped style={{ marginTop: '20px' }}>
            <thead className="card-header">
              <tr>
                <th>Name</th>
                <th>Token</th>
                <th>Last Used</th>
                <th>Created</th>
                <th>Status</th>
                <th>Delete</th>
              </tr>
            </thead>
            <tbody>
              {keys?.length ? (
                keys.map(key => {
                  return (
                    <tr key={key.id} id={key.id} className="font-weight-bold" data-testid={'key-row'}>
                      <td>
                        <div className="py-2">{key.name}</div>
                      </td>
                      <td>
                        <InputGroup>
                          <Input type={isShow === key.id ? 'text' : 'password'} value={key.value} readOnly />
                          <Button value={key.id} onClick={e => toggleShow(e.currentTarget.value)} className="ml-2">
                            {isShow !== key.id ? (
                              <>
                                <FontAwesomeIcon icon={'eye'} />
                              </>
                            ) : (
                              'hide'
                            )}
                          </Button>
                        </InputGroup>
                      </td>
                      <td>
                        <div className="py-2" data-testid={'key-last-updated'}>
                          {key.lastUpdatedDate}
                        </div>
                      </td>
                      <td>
                        <div className="py-2" data-testid={'key-created-date'}>
                          {key.createdDate}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center justify-content-center">
                          <div
                            className="rounded-circle mr-2"
                            style={{
                              width: '10px',
                              height: '10px',
                              backgroundColor: key.enabled ? '#28a745' : '#6c757d'
                            }}
                          />
                          <Button
                            onClick={() => toggleKeyStatus(key.id, key.enabled)}
                            disabled={actionsDisabled}
                            color={key.enabled ? 'success' : 'secondary'}
                            size="sm"
                          >
                            {key.enabled ? 'Enabled' : 'Disabled'}
                          </Button>
                        </div>
                      </td>
                      <td>
                        <>
                          <Button
                            onClick={e => toggleAlert(e.currentTarget.value, e.currentTarget.name)}
                            value={key.id}
                            disabled={actionsDisabled}
                            name={key.name}
                            color="falcon-danger"
                            block
                            data-testid={'key-delete-alert'}
                          >
                            <FontAwesomeIcon icon={'trash'} color={'#bd472a'} value={key.id} />
                          </Button>
                        </>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr className="font-weight-bold">
                  <td>You have no keys yet</td>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              )}

              <tr />
            </tbody>
          </Table>
          <div className="text-right">
            <InputModal
              cta={'Create key'}
              isReadOnly={actionsDisabled}
              handleSave={handleSave}
              setKeyName={setKeyName}
              keyName={keyName}
              isSubmitted={isSubmitted}
              setIsSubmitted={setIsSubmitted}
              modalOpen={modalOpen}
              setModalOpen={setModalOpen}
              setIsLoading={setIsLoading}
              createKey={createKey}
              loader={loader}
            />
            <AlertModal
              deleteKey={deleteKey}
              showAlert={showAlert}
              setShowAlert={setShowAlert}
              loader={loader}
              keyId={keyId}
              keyName={keyName}
            />
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default OpenApiSettings;
